import type { HTMLAttributes } from 'react';
import { useEffect, useMemo } from 'react';

import Divider from 'components/divider';
import { useTalkPageContext } from 'components/pages/talks/slug/talk-page-context';
import useVideoPlayerStore from 'components/video-player/store';

import SubtitlesPanelProps from './subtitles.props';
import SubtitlesBody from './subtitlesBody';
import SubtitlesFooter from './subtitlesFooter';
import SubtitlesHeader from './subtitlesHeader';

const SubtitlesPanel = ({
  languages,
  ...props
}: SubtitlesPanelProps & HTMLAttributes<HTMLElement>) => {
  const { subtitle: querySubtitle } = useTalkPageContext();
  const videoSubtitle = useVideoPlayerStore(state => state.subtitle);
  const initialSubtitle = querySubtitle || videoSubtitle || 'OFF';

  const requestSubtitleLanguage = useVideoPlayerStore(
    state => state.requestSubtitleLanguage
  );

  const deduplicatedLanguages = useMemo(() => {
    const uniqueLanguages = new Map();
    languages.forEach(lang => {
      if (!uniqueLanguages.has(lang.languageCode)) {
        uniqueLanguages.set(lang.languageCode, lang);
      }
    });
    return Array.from(uniqueLanguages.values());
  }, [languages]);

  useEffect(() => {
    if (initialSubtitle) requestSubtitleLanguage(initialSubtitle);
  }, [initialSubtitle, requestSubtitleLanguage]);

  return (
    <div
      className="sm:max-h-1/2-screen sm:w-screen sm:overflow-auto sm:rounded-none md:w-full md:overflow-hidden md:rounded-sm"
      data-testid="subtitles-panel"
      {...props}
    >
      <SubtitlesHeader languagesLength={deduplicatedLanguages.length} />

      <div className="w-full px-4 pl-6 sm:h-full">
        <Divider className="w-full" />
      </div>

      <SubtitlesBody languages={deduplicatedLanguages} />
      <div className="w-full px-4 pl-6 sm:h-full">
        <Divider className="w-full sm:bg-white" />
      </div>
      <SubtitlesFooter />
    </div>
  );
};

export default SubtitlesPanel;
